import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../supabaseClient';
import AdminLogin from '../components/AdminLogin';
import AdminPanel from '../components/AdminPanel';

const AdminContainer = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.colors.background};
  padding: ${props => props.theme.spacing.xl};
`;

const Header = styled.header`
  max-width: 1400px;
  margin: 0 auto ${props => props.theme.spacing.xl};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.md};
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LogoIcon = styled.div`
  width: 40px;
  height: 40px;
  background: ${props => props.theme.gradients.primary};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: white;
  font-size: 1.2rem;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: ${props => props.theme.typography.weights.bold};
  color: ${props => props.theme.colors.textLight};
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

const UserBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: ${props => props.theme.colors.surfaceCard};
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  border-radius: 100px;
  font-size: 0.875rem;
  color: ${props => props.theme.colors.subtextGray};
`;

const UserDot = styled.span`
  width: 8px;
  height: 8px;
  background: #22C55E;
  border-radius: 50%;
`;

const LogoutButton = styled.button`
  padding: 10px 18px;
  background: transparent;
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  color: ${props => props.theme.colors.textDark};
  border-radius: 10px;
  font-weight: 500;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${props => props.theme.colors.accentPurple};
    color: ${props => props.theme.colors.accentPurple};
  }
`;

const MainContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.xl};

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled(motion.div)`
  background: ${props => props.theme.colors.surfaceCard};
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  border-radius: 16px;
  padding: ${props => props.theme.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  background: ${props => props.bg || props.theme.gradients.subtle};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
`;

const StatInfo = styled.div``;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: ${props => props.theme.typography.weights.bold};
  color: ${props => props.theme.colors.textLight};
`;

const StatLabel = styled.div`
  font-size: 0.8125rem;
  color: ${props => props.theme.colors.subtextGray};
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const SearchBox = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 44px;
  background: ${props => props.theme.colors.surfaceCard};
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  border-radius: 12px;
  font-size: 0.9375rem;
  color: ${props => props.theme.colors.textLight};
  transition: all 0.2s;

  &::placeholder {
    color: ${props => props.theme.colors.subtextGray};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accentPurple};
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.theme.colors.subtextGray};
`;

const FilterTabs = styled.div`
  display: flex;
  background: ${props => props.theme.colors.surfaceCard};
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  border-radius: 12px;
  padding: 4px;
`;

const FilterTab = styled.button`
  padding: 8px 16px;
  background: ${props => props.active ? props.theme.gradients.primary : 'transparent'};
  color: ${props => props.active ? 'white' : props.theme.colors.subtextGray};
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: ${props => props.active ? 'white' : props.theme.colors.textLight};
  }
`;

const CreateButton = styled(motion.button)`
  padding: 12px 24px;
  background: ${props => props.theme.gradients.primary};
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.9375rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: ${props => props.theme.shadows.glow};
`;

const BlogGrid = styled.div`
  display: grid;
  gap: ${props => props.theme.spacing.md};
`;

const BlogCard = styled(motion.div)`
  background: ${props => props.theme.colors.surfaceCard};
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  border-radius: 16px;
  padding: ${props => props.theme.spacing.lg};
  display: grid;
  grid-template-columns: 80px 1fr auto;
  gap: ${props => props.theme.spacing.lg};
  align-items: center;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${props => props.theme.colors.accentPurple};
    box-shadow: ${props => props.theme.shadows.card};
  }

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.md};
  }
`;

const BlogThumb = styled.div`
  width: 80px;
  height: 60px;
  background: ${props => props.src ? `url(${props.src})` : props.theme.gradients.subtle};
  background-size: cover;
  background-position: center;
  border-radius: 10px;

  @media (max-width: 700px) {
    width: 100%;
    height: 120px;
  }
`;

const BlogInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const BlogTitle = styled.h3`
  font-size: 1rem;
  font-weight: ${props => props.theme.typography.weights.semibold};
  color: ${props => props.theme.colors.textLight};
  margin: 0 0 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BlogMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  flex-wrap: wrap;
  font-size: 0.8125rem;
  color: ${props => props.theme.colors.subtextGray};
`;

const BlogActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};

  @media (max-width: 700px) {
    justify-content: flex-end;
  }
`;

const StatusBadge = styled.span`
  padding: 6px 12px;
  border-radius: 100px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  background: ${props => {
    if (props.status === 'published') return 'rgba(34, 197, 94, 0.15)';
    if (props.status === 'draft') return 'rgba(234, 179, 8, 0.15)';
    return 'rgba(156, 163, 175, 0.15)';
  }};
  color: ${props => {
    if (props.status === 'published') return '#22C55E';
    if (props.status === 'draft') return '#EAB308';
    return '#9CA3AF';
  }};
`;

const FeaturedBadge = styled.span`
  padding: 6px 12px;
  border-radius: 100px;
  font-size: 0.75rem;
  font-weight: 600;
  background: rgba(139, 92, 246, 0.15);
  color: ${props => props.theme.colors.accentPurple};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ActionButton = styled.button`
  width: 36px;
  height: 36px;
  background: transparent;
  border: 1px solid ${props => props.theme.colors.borderSubtle};
  border-radius: 10px;
  color: ${props => props.theme.colors.subtextGray};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    border-color: ${props => props.danger ? '#EF4444' : props.theme.colors.accentPurple};
    color: ${props => props.danger ? '#EF4444' : props.theme.colors.accentPurple};
    background: ${props => props.danger ? 'rgba(239, 68, 68, 0.1)' : props.theme.gradients.subtle};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xxxl};
  color: ${props => props.theme.colors.subtextGray};
`;

const EmptyIcon = styled.div`
  font-size: 3rem;
  margin-bottom: ${props => props.theme.spacing.md};
  opacity: 0.5;
`;

const LoadingSpinner = styled(motion.div)`
  width: 40px;
  height: 40px;
  border: 3px solid ${props => props.theme.colors.borderSubtle};
  border-top-color: ${props => props.theme.colors.accentPurple};
  border-radius: 50%;
  margin: 100px auto;
`;

/* ── waitlist-specific styles ── */
const TabBar = styled.div`
  display: flex;
  gap: 0;
  background: ${p => p.theme.colors.surfaceCard};
  border: 1px solid ${p => p.theme.colors.borderSubtle};
  border-radius: 14px;
  padding: 4px;
  margin-bottom: ${p => p.theme.spacing.xl};
  max-width: 320px;
`;

const Tab = styled.button`
  flex: 1;
  padding: 10px 20px;
  background: ${p => p.active ? p.theme.gradients.primary : 'transparent'};
  color: ${p => p.active ? 'white' : p.theme.colors.subtextGray};
  border: none;
  border-radius: 10px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: ${p => p.active ? 'white' : p.theme.colors.textLight};
  }
`;

const WaitlistTable = styled.div`
  background: ${p => p.theme.colors.surfaceCard};
  border: 1px solid ${p => p.theme.colors.borderSubtle};
  border-radius: 16px;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 200px;
  padding: 14px 24px;
  background: ${p => p.theme.gradients.subtle};
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${p => p.theme.colors.subtextGray};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid ${p => p.theme.colors.borderSubtle};

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    & > span:last-child { display: none; }
  }
`;

const TableRow = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr 200px;
  padding: 14px 24px;
  font-size: 0.9375rem;
  color: ${p => p.theme.colors.textLight};
  border-bottom: 1px solid ${p => p.theme.colors.borderSubtle};
  transition: background 0.15s;

  &:last-child { border-bottom: none; }
  &:hover { background: ${p => p.theme.gradients.subtle}; }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    & > span:last-child { display: none; }
  }
`;

const Admin = () => {
  const [session, setSession] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [showPanel, setShowPanel] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('blogs');
  const [waitlist, setWaitlist] = useState([]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (session) {
      fetchBlogs();
      fetchWaitlist();
    }
  }, [session]);

  const fetchBlogs = async () => {
    try {
      const { data, error } = await supabase
        .from('blogs')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBlogs(data || []);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    }
  };

  const fetchWaitlist = async () => {
    try {
      const { data, error } = await supabase
        .from('waitlist')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setWaitlist(data || []);
    } catch (error) {
      console.error('Error fetching waitlist:', error);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
  };

  const handleEditBlog = (blog) => {
    setSelectedBlog(blog);
    setShowPanel(true);
  };

  const handleCreateNew = () => {
    setSelectedBlog(null);
    setShowPanel(true);
  };

  const toggleFeatured = async (e, blog) => {
    e.stopPropagation();
    try {
      const { error } = await supabase
        .from('blogs')
        .update({ featured: !blog.featured })
        .eq('id', blog.id);

      if (error) throw error;
      fetchBlogs();
    } catch (error) {
      console.error('Error toggling featured:', error);
    }
  };

  const handleDelete = async (e, blog) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${blog.title}"? This cannot be undone.`)) return;

    try {
      const { error } = await supabase
        .from('blogs')
        .delete()
        .eq('id', blog.id);

      if (error) throw error;
      fetchBlogs();
    } catch (error) {
      console.error('Error deleting blog:', error);
    }
  };

  // Filter and search blogs
  const filteredBlogs = blogs.filter(blog => {
    const matchesSearch = blog.title?.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filter === 'all' ||
      (filter === 'published' && blog.status === 'published') ||
      (filter === 'draft' && blog.status === 'draft') ||
      (filter === 'featured' && blog.featured);
    return matchesSearch && matchesFilter;
  });

  // Stats
  const stats = {
    total: blogs.length,
    published: blogs.filter(b => b.status === 'published').length,
    drafts: blogs.filter(b => b.status === 'draft').length,
    featured: blogs.filter(b => b.featured).length,
  };

  if (loading) {
    return (
      <AdminContainer>
        <LoadingSpinner
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        />
      </AdminContainer>
    );
  }

  if (!session) {
    return (
      <AdminContainer style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <AdminLogin onLogin={setSession} />
      </AdminContainer>
    );
  }

  return (
    <AdminContainer>
      <Header>
        <Logo>
          <LogoIcon>N</LogoIcon>
          <Title>Neal Admin</Title>
        </Logo>
        <HeaderActions>
          <UserBadge>
            <UserDot />
            {session?.user?.email?.split('@')[0]}
          </UserBadge>
          <LogoutButton onClick={handleLogout}>Sign Out</LogoutButton>
        </HeaderActions>
      </Header>

      <MainContent>
        <TabBar>
          <Tab active={activeTab === 'blogs'} onClick={() => setActiveTab('blogs')}>Blogs</Tab>
          <Tab active={activeTab === 'waitlist'} onClick={() => setActiveTab('waitlist')}>Waitlist</Tab>
        </TabBar>

        {activeTab === 'blogs' && (
          <>
            <StatsRow>
              <StatCard whileHover={{ y: -2 }}>
                <StatIcon bg="linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)">📝</StatIcon>
                <StatInfo>
                  <StatValue>{stats.total}</StatValue>
                  <StatLabel>Total Blogs</StatLabel>
                </StatInfo>
              </StatCard>
              <StatCard whileHover={{ y: -2 }}>
                <StatIcon bg="linear-gradient(135deg, #22C55E 0%, #4ADE80 100%)">✓</StatIcon>
                <StatInfo>
                  <StatValue>{stats.published}</StatValue>
                  <StatLabel>Published</StatLabel>
                </StatInfo>
              </StatCard>
              <StatCard whileHover={{ y: -2 }}>
                <StatIcon bg="linear-gradient(135deg, #EAB308 0%, #FDE047 100%)">✎</StatIcon>
                <StatInfo>
                  <StatValue>{stats.drafts}</StatValue>
                  <StatLabel>Drafts</StatLabel>
                </StatInfo>
              </StatCard>
              <StatCard whileHover={{ y: -2 }}>
                <StatIcon bg="linear-gradient(135deg, #EC4899 0%, #F472B6 100%)">⭐</StatIcon>
                <StatInfo>
                  <StatValue>{stats.featured}</StatValue>
                  <StatLabel>Featured</StatLabel>
                </StatInfo>
              </StatCard>
            </StatsRow>

            <Toolbar>
              <SearchBox>
                <SearchIcon>🔍</SearchIcon>
                <SearchInput
                  placeholder="Search blogs..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </SearchBox>
              <FilterTabs>
                <FilterTab active={filter === 'all'} onClick={() => setFilter('all')}>All</FilterTab>
                <FilterTab active={filter === 'published'} onClick={() => setFilter('published')}>Published</FilterTab>
                <FilterTab active={filter === 'draft'} onClick={() => setFilter('draft')}>Drafts</FilterTab>
                <FilterTab active={filter === 'featured'} onClick={() => setFilter('featured')}>Featured</FilterTab>
              </FilterTabs>
              <CreateButton
                onClick={handleCreateNew}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                ✨ Create Blog
              </CreateButton>
            </Toolbar>

            <BlogGrid>
              <AnimatePresence>
                {filteredBlogs.map((blog) => (
                  <BlogCard
                    key={blog.id}
                    onClick={() => handleEditBlog(blog)}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    layout
                  >
                    <BlogThumb src={blog.thumbnail_url} />
                    <BlogInfo>
                      <BlogTitle>{blog.title || 'Untitled'}</BlogTitle>
                      <BlogMeta>
                        <span>{new Date(blog.created_at).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric'
                        })}</span>
                        <span>•</span>
                        <span>{blog.content?.split(/\s+/).length || 0} words</span>
                        {blog.featured && (
                          <>
                            <span>•</span>
                            <FeaturedBadge>⭐ Featured</FeaturedBadge>
                          </>
                        )}
                      </BlogMeta>
                    </BlogInfo>
                    <BlogActions>
                      <StatusBadge status={blog.status}>{blog.status}</StatusBadge>
                      <ActionButton
                        onClick={(e) => toggleFeatured(e, blog)}
                        title={blog.featured ? 'Remove from featured' : 'Add to featured'}
                        style={{ color: blog.featured ? '#8B5CF6' : undefined }}
                      >
                        ⭐
                      </ActionButton>
                      <ActionButton
                        danger
                        onClick={(e) => handleDelete(e, blog)}
                        title="Delete blog"
                      >
                        🗑
                      </ActionButton>
                    </BlogActions>
                  </BlogCard>
                ))}
              </AnimatePresence>

              {filteredBlogs.length === 0 && (
                <EmptyState>
                  <EmptyIcon>📝</EmptyIcon>
                  <h3>No blogs found</h3>
                  <p>{search ? 'Try a different search term' : 'Create your first blog post!'}</p>
                </EmptyState>
              )}
            </BlogGrid>
          </>
        )}

        {activeTab === 'waitlist' && (
          <>
            <StatsRow style={{ gridTemplateColumns: 'repeat(2, 1fr)', maxWidth: 500 }}>
              <StatCard whileHover={{ y: -2 }}>
                <StatIcon bg="linear-gradient(135deg, #06B6D4 0%, #22D3EE 100%)">📧</StatIcon>
                <StatInfo>
                  <StatValue>{waitlist.length}</StatValue>
                  <StatLabel>Total Signups</StatLabel>
                </StatInfo>
              </StatCard>
              <StatCard whileHover={{ y: -2 }}>
                <StatIcon bg="linear-gradient(135deg, #22C55E 0%, #4ADE80 100%)">📈</StatIcon>
                <StatInfo>
                  <StatValue>
                    {waitlist.filter(w =>
                      new Date(w.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
                    ).length}
                  </StatValue>
                  <StatLabel>This Week</StatLabel>
                </StatInfo>
              </StatCard>
            </StatsRow>

            <WaitlistTable>
              <TableHeader>
                <span>Email</span>
                <span>Date Joined</span>
              </TableHeader>
              {waitlist.map((entry, i) => (
                <TableRow
                  key={entry.id}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.02 }}
                >
                  <span>{entry.email}</span>
                  <span style={{ color: 'inherit', opacity: 0.6 }}>
                    {new Date(entry.created_at).toLocaleDateString('en-US', {
                      month: 'short', day: 'numeric', year: 'numeric',
                      hour: '2-digit', minute: '2-digit'
                    })}
                  </span>
                </TableRow>
              ))}
              {waitlist.length === 0 && (
                <EmptyState>
                  <EmptyIcon>📧</EmptyIcon>
                  <h3>No waitlist signups yet</h3>
                  <p>Signups from the website will appear here.</p>
                </EmptyState>
              )}
            </WaitlistTable>
          </>
        )}
      </MainContent>

      <AdminPanel
        isOpen={showPanel}
        onClose={() => setShowPanel(false)}
        onPublish={() => {
          fetchBlogs();
          setShowPanel(false);
        }}
        session={session}
        existingBlog={selectedBlog}
      />
    </AdminContainer>
  );
};

export default Admin;
